/**
 * §5.7 (docs/KONZEPT-ANFRAGE-ZU-ANGEBOT.md) — was der Betrieb direkt nach dem Import erfährt.
 *
 * DAS PROBLEM, das dieser Kasten schließt: Der Import meldete bisher ausschließlich Erfolg —
 * „N Positionen sind jetzt in deinem Katalog verfügbar." Dass ein Teil davon mit 0,00 € dort
 * steht, stand nirgends. Der Betrieb sah ein Ergebnis und vertraute ihm; die Lücke tauchte erst
 * Wochen später im Angebot auf (Referenzfall: 10 von 11 Positionen zu 0,00 €).
 *
 * ZWEI AUSSAGEN, NIE VERMISCHT — das ist der ganze Trick:
 *  - Menü-Gänge ohne Einzelpreis sind KORREKT (ihr Preis hängt am Menü). Sie als Fehler zu
 *    zeigen, würde Misstrauen gegen richtige Daten erzeugen (Konzept § 5.7, wörtlich).
 *  - Alles andere ohne Preis ist eine echte Lücke.
 * Welche Zeile in welche Gruppe gehört, entscheidet NICHT diese Komponente, sondern die eine
 * geteilte Regel `ungedeckteNullpreisKatalogPositionen()` in `@maestro/pricing`; hier kommen nur
 * noch die fertigen Zahlen und Abschnittsnamen vom Server an.
 *
 * BESCHREIBEND, NICHT BEWERTEND: Der Text sagt, was im Katalog steht und was daraus folgt. Er
 * urteilt nicht über das Dokument des Betriebs — eine Karte ohne Preise ist oft völlig richtig
 * (Tagesangebot, Menü), nur eben nicht angebotsfähig.
 *
 * UND ER BENENNT, WAS FEHLT: „3 Positionen ohne Preis" schickt den Betrieb auf die Suche.
 * „3 Positionen ohne Preis — in: Vorspeisen, Desserts, Getränke" ist eine Handlungsanweisung.
 */
import { Icon } from "./components";

export interface ImportPreisHinweisProps {
  /** Echte Preislücken (0,00 € ohne erklärenden Vermerk). */
  zeroPriceCount?: number;
  /** Menü-Gänge, deren Preis am Menü hängt — korrekt, nur erklärungsbedürftig. */
  zeroPriceCoveredCount?: number;
  /** Abschnitte der Karte, in denen die Lücken stehen — in der Reihenfolge des Dokuments. */
  sectionsWithoutPrice?: string[];
}

/** Zählt Abschnitte auf, ohne bei einer langen Karte die Meldung zu sprengen. */
function abschnitteInWorten(sections: string[]): string {
  if (sections.length === 0) return "";
  if (sections.length <= 4) return sections.join(", ");
  return `${sections.slice(0, 4).join(", ")} und ${sections.length - 4} weitere`;
}

export function ImportPreisHinweis({ zeroPriceCount = 0, zeroPriceCoveredCount = 0, sectionsWithoutPrice = [] }: ImportPreisHinweisProps) {
  // Nichts zu melden ist eine gute Nachricht — aber keine, für die ein Kasten nötig wäre.
  if (zeroPriceCount <= 0 && zeroPriceCoveredCount <= 0) return null;

  return (
    <div className="space-y-2">
      {zeroPriceCount > 0 && (
        <div className="flex gap-2.5 rounded-lg bg-secondary/10 px-3 py-2.5 text-left font-body-sm text-body-sm text-secondary dark:text-[#b8ae9e]">
          <Icon name="price_change" className="mt-0.5 shrink-0 text-[18px]" />
          <div className="space-y-0.5">
            <p className="font-semibold">
              {zeroPriceCount === 1 ? "1 Position wurde ohne Preis übernommen" : `${zeroPriceCount} Positionen wurden ohne Preis übernommen`}
              {sectionsWithoutPrice.length > 0 && <> — in: {abschnitteInWorten(sectionsWithoutPrice)}</>}
            </p>
            <p className="opacity-90">
              Sie stehen mit 0,00 € im Katalog und würden so auch in ein Angebot übernommen. Preis im Katalog nachtragen.
            </p>
          </div>
        </div>
      )}

      {zeroPriceCoveredCount > 0 && (
        <div className="flex gap-2.5 rounded-lg bg-surface-container-low px-3 py-2.5 text-left font-body-sm text-body-sm text-on-surface-variant dark:bg-surface-variant/10 dark:text-secondary-fixed-dim">
          <Icon name="restaurant_menu" className="mt-0.5 shrink-0 text-[18px]" />
          <p>
            {zeroPriceCoveredCount === 1 ? "1 Menü-Gang hat keinen Einzelpreis" : `${zeroPriceCoveredCount} Menü-Gänge haben keinen Einzelpreis`} — das ist so
            richtig: Ihr Preis steht am Menü.
          </p>
        </div>
      )}
    </div>
  );
}
